import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VendorBooking() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchBooking = async () => {
    try {
      const { data } = await axios.get(`${API}/vendor/booking/${token}`);
      setBooking(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not load this booking.");
    }
  };

  useEffect(() => { fetchBooking(); /* eslint-disable-next-line */ }, [token]);

  const setStatus = async (status) => {
    setSaving(true);
    try {
      await axios.post(`${API}/vendor/booking/${token}/status`, { status });
      setBooking((b) => ({ ...b, status }));
      toast.success(`Status updated to "${status}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const uploadReport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/vendor/booking/${token}/report`, fd);
      setBooking((b) => ({ ...b, has_report: true, report_filename: data.report_filename }));
      toast.success("Report uploaded — the patient has been notified");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-emerald-950 p-6">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center" data-testid="vendor-error">
        <p className="text-lg font-semibold text-emerald-950">Link unavailable</p>
        <p className="text-sm text-zinc-600 mt-2">{error}</p>
      </div>
    </div>
  );

  if (!booking) return <div className="min-h-screen flex items-center justify-center bg-emerald-950 text-white">Loading…</div>;

  return (
    <div className="min-h-screen bg-emerald-950 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md" data-testid="vendor-booking-page">
        <p className="text-xs uppercase tracking-wide text-emerald-600 font-semibold">Lupin Diagnostics · Home Collection</p>
        <h1 className="font-heading text-2xl font-semibold text-emerald-950 mt-1">{booking.booking_number}</h1>
        <div className="mt-4 space-y-1.5 text-sm text-zinc-700">
          <p><b>Patient:</b> {booking.name} {booking.age ? `· Age ${booking.age}` : ""}</p>
          <p><b>Phone:</b> {booking.phone}</p>
          <p><b>Address:</b> {booking.address}, {booking.pincode}</p>
          <p><b>Preferred:</b> {booking.preferred_date} · {booking.preferred_time}</p>
        </div>
        <div className="mt-5">
          <p className="text-xs text-zinc-500 mb-2">Current status</p>
          <span data-testid="vendor-current-status" className="inline-block text-sm font-semibold px-3 py-1 rounded-full bg-emerald-100 text-emerald-800">{booking.status}</span>
        </div>
        <div className="mt-6">
          <p className="text-xs text-zinc-500 mb-2">Update status</p>
          <div className="grid grid-cols-2 gap-2">
            {booking.statuses.map((s) => (
              <button key={s} disabled={saving || s === booking.status} data-testid={`vendor-set-${s.toLowerCase()}`}
                onClick={() => setStatus(s)}
                className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${s === booking.status ? "bg-emerald-600 text-white" : "border border-emerald-300 text-emerald-800 hover:bg-emerald-50"} disabled:opacity-60`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-6 border-t border-zinc-100 pt-5">
          <p className="text-xs text-zinc-500 mb-2">Upload report (PDF/image)</p>
          {booking.has_report ? (
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-2" data-testid="vendor-report-uploaded">
              ✓ Report uploaded{booking.report_filename ? `: ${booking.report_filename}` : ""}. The patient can download it in their chat.
            </p>
          ) : (
            <p className="text-[11px] text-zinc-400 mb-2">Upload the finished report — the patient is notified and can download it inside the Aarogya Assist chat.</p>
          )}
          <label className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium cursor-pointer transition-colors ${uploading ? "opacity-60" : "bg-emerald-600 text-white hover:bg-emerald-700"}`} data-testid="vendor-report-upload-label">
            {uploading ? "Uploading…" : booking.has_report ? "Replace report" : "Upload report"}
            <input type="file" accept=".pdf,image/*" className="hidden" disabled={uploading}
              onChange={uploadReport} data-testid="vendor-report-input" />
          </label>
        </div>
        <p className="mt-6 text-[11px] text-zinc-400">Updating here reflects instantly in the corporate HR dashboard.</p>
      </div>
    </div>
  );
}
